import { useState } from "react";
import {
  FaBrain,
  FaExclamationTriangle,
  FaFlask,
  FaStar,
  FaStethoscope,
  FaSyncAlt,
  FaTools,
  FaTruck,
} from "react-icons/fa";
import chemicalPlantsThumbnail from "../assets/chemicalPlants.png";
import beyondAerospaceThumbnail from "../assets/effie-intro-thumbnail.png";
import beyondAerospace from "../assets/effieintro.webm";
import supplyChainThumbnail from "../assets/supplyChain.png";

const AIInfusedChatbotSection = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [activeVideoTab, setActiveVideoTab] = useState(0);
  const tabs = [
    {
      title: "Value Proposition",
      icon: <FaStar />,
      content: (
        <div className="grid grid-cols-1 gap-4 text-left">
          <ul className="space-y-2">
            <li className="flex items-start">
              <span className="text-cyan-500 mr-2">•</span>
              <span className="text-base sm:text-lg">
                <strong className="text-gray-800">
                  Enhanced Patient Engagement:
                </strong>{" "}
                By offering personalized, 24/7 support through chatbots,
                patients receive timely assistance, improving their overall
                experience.
              </span>
            </li>
            <li className="flex items-start">
              <span className="text-cyan-500 mr-2">•</span>
              <span className="text-base sm:text-lg">
                <strong className="text-gray-800">Cost Reduction:</strong>{" "}
                Automating appointment scheduling and handling FAQs reduces
                administrative costs and minimizes missed appointments.
              </span>
            </li>
            <li className="flex items-start">
              <span className="text-cyan-500 mr-2">•</span>
              <span className="text-base sm:text-lg">
                <strong className="text-gray-800">Improved Efficiency:</strong>{" "}
                Streamlined workflows lead to better resource allocation and
                faster response times, particularly in emergency situations.
              </span>
            </li>
            <li className="flex items-start">
              <span className="text-cyan-500 mr-2">•</span>
              <span className="text-base sm:text-lg">
                <strong className="text-gray-800">
                  Data Security and Compliance:
                </strong>{" "}
                Assured adherence to GDPR and HIPAA standards builds trust with
                customers concerned about data privacy.
              </span>
            </li>
            <li className="flex items-start">
              <span className="text-cyan-500 mr-2">•</span>
              <span className="text-base sm:text-lg">
                Ensuring an accurate representation of data while safeguarding
                the intellectual property of clinics is essential. We strive to
                help clinics present themselves in a manner that aligns with
                their desired image for patients and clients.
              </span>
            </li>
          </ul>
        </div>
      ),
    },
    {
      title: "Key Features",
      icon: <FaBrain />,
      content: (
        <div className="grid grid-cols-1 gap-4">
          <p className="text-base sm:text-lg text-gray-600">
            Our AI-infused chatbot leverages large language models (LLM),
            retrieval-augmented generation (RAG), and self-correction to deliver
            human-like, accurate responses.
          </p>
          <ul className="space-y-2">
            <li className="flex items-start">
              <span className="text-cyan-500 mr-2">•</span>
              <span className="text-base sm:text-lg">
                <strong className="text-gray-800">Task-Specific:</strong>{" "}
                Handles customer support, scheduling, and more with contextual
                awareness.
              </span>
            </li>
            <li className="flex items-start">
              <span className="text-cyan-500 mr-2">•</span>
              <span className="text-base sm:text-lg">
                <strong className="text-gray-800">LLM-Powered:</strong>{" "}
                Simulates natural conversations with advanced NLP.
              </span>
            </li>
            <li className="flex items-start">
              <span className="text-cyan-500 mr-2">•</span>
              <span className="text-base sm:text-lg">
                <strong className="text-gray-800">RAG Integration:</strong>{" "}
                Fetches real-time, domain-specific data for accurate answers.
              </span>
            </li>
            <li className="flex items-start">
              <span className="text-cyan-500 mr-2">•</span>
              <span className="text-base sm:text-lg">
                <strong className="text-gray-800">
                  Hallucination Awareness:
                </strong>{" "}
                Detects and flags potential inaccuracies.
              </span>
            </li>
            <li className="flex items-start">
              <span className="text-cyan-500 mr-2">•</span>
              <span className="text-base sm:text-lg">
                <strong className="text-gray-800">Expert Escalation:</strong>{" "}
                Routes complex queries to human experts.
              </span>
            </li>
          </ul>
        </div>
      ),
    },
    {
      title: "Continuous Improvement",
      icon: <FaSyncAlt />,
      content: (
        <div className="grid grid-cols-1 gap-4">
          <p className="text-base sm:text-lg text-gray-600">
            The chatbot evolves over time, refining its capabilities through
            dynamic updates and feedback loops.
          </p>
          <ul className="space-y-2">
            <li className="flex items-start">
              <span className="text-cyan-500 mr-2">•</span>
              <span className="text-base sm:text-lg">
                Updates knowledge base with expert feedback.
              </span>
            </li>
            <li className="flex items-start">
              <span className="text-cyan-500 mr-2">•</span>
              <span className="text-base sm:text-lg">
                Incorporates validated data into training pipelines.
              </span>
            </li>
            <li className="flex items-start">
              <span className="text-cyan-500 mr-2">•</span>
              <span className="text-base sm:text-lg">
                Combines LLM and RAG for tailored, accurate responses.
              </span>
            </li>
          </ul>
        </div>
      ),
    },
    {
      title: "Hallucination Handling",
      icon: <FaExclamationTriangle />,
      content: (
        <div className="grid grid-cols-1 gap-4">
          <p className="text-base sm:text-lg text-gray-600">
            Our chatbot detects and corrects potential inaccuracies to ensure
            reliable responses.
          </p>
          <ul className="space-y-2">
            <li className="flex items-start">
              <span className="text-cyan-500 mr-2">•</span>
              <span className="text-base sm:text-lg">
                <strong className="text-gray-800">Detection:</strong> Flags
                uncertain or fabricated responses.
              </span>
            </li>
            <li className="flex items-start">
              <span className="text-cyan-500 mr-2">•</span>
              <span className="text-base sm:text-lg">
                <strong className="text-gray-800">Notification:</strong> Alerts
                users with a disclaimer.
              </span>
            </li>
            <li className="flex items-start">
              <span className="text-cyan-500 mr-2">•</span>
              <span className="text-base sm:text-lg">
                <strong className="text-gray-800">Expert Query:</strong>{" "}
                Escalates to experts for verification.
              </span>
            </li>
            <li className="flex items-start">
              <span className="text-cyan-500 mr-2">•</span>
              <span className="text-base sm:text-lg">
                <strong className="text-gray-800">Knowledge Update:</strong>{" "}
                Stores verified answers.
              </span>
            </li>
            <li className="flex items-start">
              <span className="text-cyan-500 mr-2">•</span>
              <span className="text-base sm:text-lg">
                <strong className="text-gray-800">Model Update:</strong>{" "}
                Retrains periodically for accuracy.
              </span>
            </li>
          </ul>
        </div>
      ),
    },
    {
      title: "Examples",
      icon: <FaStethoscope />,
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-50 p-4 sm:p-6 rounded-lg shadow-sm hover:shadow-md transition-all animate-fade-in">
            <div className="flex items-center justify-center mb-4">
              <FaStethoscope size={32} className="text-cyan-500" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
              Healthcare
            </h3>
            <p className="text-base sm:text-lg text-gray-600">
              Detects uncertainty in medical queries, flags responses, and
              escalates to certified professionals for accurate answers.
            </p>
          </div>
          <div className="bg-gray-50 p-4 sm:p-6 rounded-lg shadow-sm hover:shadow-md transition-all animate-fade-in">
            <div className="flex items-center justify-center mb-4">
              <FaTools size={32} className="text-cyan-500" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
              Technical Support
            </h3>
            <p className="text-base sm:text-lg text-gray-600">
              Integrates expert solutions into its database, delivering
              accurate, validated advice to users consistently.
            </p>
          </div>
        </div>
      ),
    },
  ];
  const videoTabs = [
    {
      title: "Clinical Companion",
      icon: <FaStethoscope />,
      source: beyondAerospace,
      thumbnail: beyondAerospaceThumbnail,
    },
    {
      title: "Supply Chain Companion",
      icon: <FaTruck />,
      source: "https://cloud.dataspeckle.com/s/KbHryzFEyXPBtHD/download",
      thumbnail: supplyChainThumbnail,
    },
    {
      title: "Chemical Plants Companion",
      icon: <FaFlask />,
      source: "https://cloud.dataspeckle.com/s/yGzTyGDaWeHppys/download",
      thumbnail: chemicalPlantsThumbnail,
    },
  ];

  return (
    <section
      id="chatbot_section"
      className="bg-gray-100 py-8 sm:py-16 px-4 sm:px-6"
    >
      <div className="max-w-6xl mx-auto text-center">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-gray-800 mb-6 animate-fade-in">
          AI-Infused Chatbot
        </h2>
        <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-4 animate-fade-in">
          Powered by LLM, RAG, and Self-Correction
        </p>
        <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-12 animate-fade-in">
          Deliver accurate, contextually relevant responses with a chatbot that
          learns, corrects, and adapts in real-time.
        </p>

        {/* Video Section */}
        <div className="max-w-3xl mx-auto mb-12 animate-fade-in">
          <div className="flex flex-col sm:flex-row flex-wrap justify-center items-center gap-2 sm:gap-4 mb-8 w-full sm:w-auto max-w-4xl mx-auto">
            {videoTabs.map((tab, index) => (
              <button
                key={index}
                className={`flex items-center justify-center min-w-[150px] sm:min-w-[180px] px-3 py-1.5 sm:px-4 sm:py-2 text-base sm:text-lg font-medium rounded-lg transition-all duration-300 transform ${
                  activeVideoTab === index
                    ? "bg-cyan-500 text-white scale-105 shadow-lg border-b-2 border-white font-semibold"
                    : "bg-gray-200 text-gray-600 hover:bg-gray-300 hover:scale-102 hover:shadow-md"
                }`}
                onClick={() => setActiveVideoTab(index)}
              >
                <span
                  className={`mr-2 ${
                    activeVideoTab === index ? "text-white" : "text-cyan-500"
                  }`}
                >
                  {tab.icon}
                </span>
                {tab.title}
              </button>
            ))}
          </div>
          <div className="relative w-full pb-[56.25%] bg-gray-900 rounded-lg overflow-hidden shadow-lg">
            <video
              className="absolute top-0 left-0 w-full h-full object-contain rounded-lg"
              controls
              playsInline
              key={activeVideoTab}
              poster={videoTabs[activeVideoTab].thumbnail}
            >
              <source
                src={videoTabs[activeVideoTab].source}
                type="video/webm"
              />
              Your browser does not support the video tag.
            </video>
          </div>
          <p className="text-sm text-gray-500 mt-2 text-center">
            Meet Effie — your intelligent AI assistant designed for seamless,
            empathetic support
          </p>
        </div>

        <div className="flex flex-col sm:flex-row flex-wrap justify-center items-center gap-2 sm:gap-4 mb-8 w-full sm:w-auto max-w-4xl mx-auto">
          {tabs.map((tab, index) => (
            <button
              key={index}
              className={`flex items-center justify-center min-w-[150px] sm:min-w-[180px] px-3 py-1.5 sm:px-4 sm:py-2 text-base sm:text-lg font-medium rounded-lg transition-all duration-300 transform ${
                activeTab === index
                  ? "bg-cyan-500 text-white scale-105 shadow-lg border-b-2 border-white font-semibold"
                  : "bg-gray-200 text-gray-600 hover:bg-gray-300 hover:scale-102 hover:shadow-md"
              }`}
              onClick={() => setActiveTab(index)}
            >
              <span
                className={`mr-2 ${
                  activeTab === index ? "text-white" : "text-cyan-500"
                }`}
              >
                {tab.icon}
              </span>
              {tab.title}
            </button>
          ))}
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md animate-fade-in">
          {tabs[activeTab].content}
        </div>
      </div>

      {/* Global Styles for Animations */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .animate-fade-in {
          animation: fade-in 1s ease-out forwards;
        }
      `}</style>
    </section>
  );
};

export default AIInfusedChatbotSection;
